import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Autorenew';

const useStyles = makeStyles(theme => ({
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    padding: theme.spacing(2, 3),
  },
  label: {
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
}));

const useRefresh = (entity: Entity) => {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  return async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(entity));
    alertApi.post({
      message: 'Refresh scheduled',
      severity: 'info',
      display: 'transient',
    });
  };
};

/** Type and tags; the tier tag is left out because the summary bar shows it. */
const Details = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const tags = (entity.metadata.tags ?? []).filter(t => !t.startsWith('tier:'));
  return (
    <Box display="flex" mt={2} style={{ gap: 48 }}>
      <Box>
        <Typography className={classes.label}>Type</Typography>
        <Typography>{String(entity.spec?.type ?? entity.kind)}</Typography>
      </Box>
      <Box>
        <Typography className={classes.label}>Tags</Typography>
        {tags.length ? (
          tags.map(tag => <Chip key={tag} size="small" label={tag} />)
        ) : (
          <Typography>None</Typography>
        )}
      </Box>
    </Box>
  );
};

/** The part of About the summary bar does not already show. */
export const AboutPanel = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const refresh = useRefresh(entity);
  const editUrl = entity.metadata.annotations?.['backstage.io/edit-url'];

  return (
    <Box className={classes.panel}>
      <Box display="flex" alignItems="flex-start">
        <Typography variant="body1" style={{ flex: 1 }}>
          {entity.metadata.description ?? 'No description yet.'}
        </Typography>
        <IconButton size="small" onClick={refresh} title="Refresh from source">
          <RefreshIcon fontSize="small" />
        </IconButton>
        {editUrl && (
          <IconButton size="small" component={Link} to={editUrl} title="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Details entity={entity} />
    </Box>
  );
};
